import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

interface ExportSchemePDFsDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  facilitiesCount: number;
  loading?: boolean;
}

export default function ExportSchemePDFsDialog({
  open,
  onClose,
  onConfirm,
  facilitiesCount,
  loading = false
}: ExportSchemePDFsDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PictureAsPdfIcon color="primary" />
          <Typography variant="h6">Export Scheme PDFs</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          This will generate a ZIP file containing one PDF per facility with all scheme dates.
        </Alert>

        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li>
            <Typography variant="body2">
              <strong>{facilitiesCount} facilities</strong> with scheme dates will be exported
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Each facility PDF will have <strong>one page per scheme date</strong>
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              PDFs include COVID data tables and scheme analysis conditions
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              The ZIP file will <strong>auto-download</strong> when ready
            </Typography>
          </li>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Generating PDFs... This may take a minute.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="primary"
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
          disabled={loading}
        >
          {loading ? 'Generating...' : 'Export PDFs'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}